import { useEffect, useMemo, useRef, useState } from 'react';
import { Reorder, useDragControls } from 'motion/react';
import { Plus, Minus } from 'lucide-react';
import { battingStyleHasEH } from '../../lib/lineup-engine';
import { tapLight, tapMedium } from '../../lib/haptics';
import { countFieldTime, changedCells, isBenched, type FieldTimeCount } from '../../lib/field-time-live';

// The whole game as one grid: players down the side in batting order, innings
// across, a position in every cell. This is how a coach reads a defensive plan
// on paper, and it keeps the inning before and the inning after in view while
// he changes one. Shared by the wizard's Defense step and the manual planner.
//
// Assigning is a swap, never an overwrite (the parent's onSetPosition runs
// swapIntoPosition): whoever held the position in that inning moves to the spot
// the incoming player just left. BENCH and EH are shared and hold any number.
//
// With `onReorder` the rows also drag to change the batting order. The batting
// number is the drag handle, so a flick anywhere else scrolls the page and
// the grid stays narrow enough for six innings on a phone.

/** "Andres Rodriguez" → "Andres R." so the name column leaves room for innings. */
export const shortName = (name: string) => {
  const [first, ...rest] = (name || '').trim().split(/\s+/);
  const last = rest[rest.length - 1];
  return last ? `${first} ${last[0]}.` : first || '?';
};

/** The positions a cell can hold, honoring the team's defensive shape. */
export const gridPositionOptions = (settings: any) => [
  ...(settings?.noPitcher ? [] : ['P']),
  ...(settings?.noCatcher ? [] : ['C']),
  '1B', '2B', '3B', 'SS', 'LF',
  ...(settings?.fourOutfielders ? ['LCF', 'RCF'] : ['CF']),
  'RF',
  ...(battingStyleHasEH(settings) ? ['EH'] : []),
  'BENCH',
];

type RowProps = {
  player: any;
  rowIdx: number;
  last: boolean;
  inningIdxs: number[];
  positionOf: (inningIdx: number, id: string) => string;
  options: string[];
  onSetPosition: (inningIdx: number, playerId: string, position: string) => void;
  draggable: boolean;
  /** Wide screens only (the Lineup Wall): totals columns, drag-to-swap, the change ripple. */
  wall?: {
    totals: FieldTimeCount | undefined;
    flash: Set<string>;
    dragFrom: string | null;
    dropTarget: string | null;
    onCellPointerDown: (e: React.PointerEvent, inningIdx: number, playerId: string) => void;
  };
};

const RowCells = ({ player, rowIdx, inningIdxs, positionOf, options, onSetPosition, wall, handle }: RowProps & { handle?: React.ReactNode }) => (
  <>
    <th scope="row" className="sticky left-0 z-10 bg-surface text-left pl-2.5 pr-1 py-1 font-normal">
      <span className="flex items-center gap-1.5 max-w-[100px]">
        {handle ?? (
          <span className="w-[16px] shrink-0 text-[11.5px] font-semibold tabular-nums text-ink-2">{rowIdx + 1}</span>
        )}
        <span className="truncate text-[13.5px] font-semibold text-ink">{shortName(player.name)}</span>
      </span>
    </th>
    {inningIdxs.map(i => {
      const pos = positionOf(i, player.id);
      const isPitcher = pos === 'P';
      const sitting = pos === 'BENCH' || pos === 'EH';
      const key = `${i}:${player.id}`;
      return (
        <td
          key={i}
          className="px-0.5 py-1"
          data-cell-inning={wall ? i : undefined}
          data-cell-player={wall ? player.id : undefined}
          onPointerDown={wall ? (e) => wall.onCellPointerDown(e, i, player.id) : undefined}
        >
          <select
            value={pos}
            onChange={(e) => onSetPosition(i, player.id, e.target.value)}
            aria-label={`${player.name}, inning ${i + 1}`}
            className={`w-full min-w-[36px] h-[32px] rounded-lg appearance-none outline-none text-[12.5px] font-bold tabular-nums [text-align-last:center] text-center transition-shadow duration-300 ${
              isPitcher
                ? 'bg-gold-soft text-gold-ink'
                : sitting
                  ? 'bg-canvas text-ink-2'
                  : 'bg-raised text-ink'
            } ${wall?.flash.has(key) ? 'ring-2 ring-inset ring-accent' : ''} ${
              wall?.dropTarget === key ? 'ring-2 ring-inset ring-ink' : ''
            } ${wall?.dragFrom === key ? 'opacity-40' : ''}`}
          >
            {options.map(o => (
              <option key={o} value={o}>{o === 'BENCH' ? 'BN' : o}</option>
            ))}
          </select>
        </td>
      );
    })}
    {wall && (
      <>
        <td className="pl-2 pr-1 py-1 text-center text-[12.5px] font-semibold tabular-nums text-ink">
          {wall.totals?.field ?? 0}
        </td>
        <td className="pl-1 pr-2.5 py-1 text-center text-[12.5px] font-semibold tabular-nums text-ink-2">
          {wall.totals?.bench ?? 0}
        </td>
      </>
    )}
  </>
);

// Separate component so each row gets its own drag controls (hooks cannot run
// in .map()). Mirrors ReorderRow: handle-only drag, lifted above neighbours
// while held, haptic on pick up and drop.
const DraggableRow = (props: RowProps) => {
  const controls = useDragControls();
  const [dragging, setDragging] = useState(false);
  return (
    <Reorder.Item
      as="tr"
      value={props.player.id}
      dragListener={false}
      dragControls={controls}
      className={`reorder-row ${props.last ? '' : 'border-b border-hairline'} ${dragging ? 'reorder-lifted bg-surface' : ''}`}
      style={{ boxShadow: dragging ? '0 8px 24px rgba(20,26,33,0.18)' : 'none' }}
      onDragStart={() => { setDragging(true); tapLight(); }}
      onDragEnd={() => { setDragging(false); tapMedium(); }}
    >
      <RowCells
        {...props}
        handle={
          <button
            onPointerDown={(e) => controls.start(e)}
            aria-label={`Drag ${props.player.name} to change the batting order`}
            className="w-[20px] h-[24px] -ml-1 shrink-0 rounded-md bg-raised text-[11.5px] font-bold tabular-nums text-ink touch-none cursor-grab"
          >
            {props.rowIdx + 1}
          </button>
        }
      />
    </Reorder.Item>
  );
};

export const DefenseGrid = ({
  innings, numInnings, players, settings, focusInning = 0,
  onSetPosition, onCopyToLaterInnings, onAddInning, onRemoveInning, onReorder, wide = false,
}: {
  innings: any[];
  numInnings: number;
  /** Batting order. */
  players: any[];
  settings: any;
  /** Inning to bring into view on arrival (0-based), e.g. one tapped on the review card. */
  focusInning?: number;
  onSetPosition: (inningIdx: number, playerId: string, position: string) => void;
  onCopyToLaterInnings: (inningIdx: number) => void;
  /** Absent when the plan is already at its longest / shortest. */
  onAddInning?: () => void;
  onRemoveInning?: () => void;
  /** When present, rows drag (by the batting number) to reorder the lineup. */
  onReorder?: (players: any[]) => void;
  /**
   * The Lineup Wall (wide screens): field and bench totals at the row ends,
   * press-and-hold-then-drag to swap two players within an inning, and a brief
   * ring on every cell a change moved. Phones get none of it, unchanged.
   */
  wide?: boolean;
}) => {
  // The inning whose header was tapped, for the copy-forward sheet.
  const [copyFrom, setCopyFrom] = useState<number | null>(null);

  const inningIdxs = Array.from({ length: numInnings }, (_, i) => i);
  const positionOf = (inningIdx: number, id: string) =>
    innings[inningIdx]?.find((e: any) => e.id === id)?.position || 'BENCH';
  const options = gridPositionOptions(settings);

  // ── Lineup Wall ────────────────────────────────────────────────────────────
  const shown = innings.slice(0, numInnings);
  const totals = useMemo(
    () => (wide ? countFieldTime(shown, players.map((p: any) => p.id)) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wide, innings, numInnings, players],
  );

  // The ripple: ring every cell a change touched (the player moved and anyone
  // it displaced) for a moment. A full rebuild changes most of the grid, so it
  // gets no ring; it would just light everything.
  const [flash, setFlash] = useState<Set<string>>(new Set());
  const prevShown = useRef(shown);
  useEffect(() => {
    const before = prevShown.current;
    prevShown.current = shown;
    if (!wide) return;
    const changed = changedCells(before, shown);
    if (changed.size === 0 || changed.size > 16) {
      // The previous ring's timer was just cleared, so clear the ring too.
      setFlash(prev => (prev.size ? new Set() : prev));
      return;
    }
    setFlash(changed);
    const t = window.setTimeout(() => setFlash(new Set()), 1600);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innings, numInnings]);

  // Drag to swap, touch only: a tap still opens the cell's picker; holding a
  // cell still for a moment picks it up, and dropping it on another cell in
  // the same inning swaps the two players. Mouse users keep the picker.
  const [drag, setDrag] = useState<{ inning: number; playerId: string; x: number; y: number; label: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const press = useRef<{ timer: number; x: number; y: number; inning: number; playerId: string; active: boolean } | null>(null);
  const dropRef = useRef<string | null>(null);
  const suppressUntil = useRef(0);
  const latest = useRef({ positionOf, onSetPosition });
  latest.current = { positionOf, onSetPosition };

  const onCellPointerDown = (e: React.PointerEvent, inningIdx: number, playerId: string) => {
    if (e.pointerType === 'mouse') return;
    const { clientX: x, clientY: y } = e;
    if (press.current) window.clearTimeout(press.current.timer);
    const timer = window.setTimeout(() => {
      const p = press.current;
      if (!p) return;
      p.active = true;
      tapLight();
      (document.activeElement as HTMLElement | null)?.blur?.();
      setDrag({ inning: p.inning, playerId: p.playerId, x: p.x, y: p.y, label: latest.current.positionOf(p.inning, p.playerId) });
    }, 280);
    press.current = { timer, x, y, inning: inningIdx, playerId, active: false };
  };

  useEffect(() => {
    if (!wide) return;
    let raf = 0;
    let lastPoint: { x: number; y: number } | null = null;
    const reset = () => {
      if (press.current) window.clearTimeout(press.current.timer);
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      lastPoint = null;
      edgeSince = 0;
      press.current = null;
      dropRef.current = null;
      setDrag(null);
      setDropTarget(null);
    };
    const hitTest = (x: number, y: number) => {
      const p = press.current;
      if (!p) return;
      const cell = (document.elementFromPoint(x, y) as HTMLElement | null)?.closest<HTMLElement>('[data-cell-player]');
      const inning = cell ? Number(cell.dataset.cellInning) : NaN;
      const target = cell && inning === p.inning && cell.dataset.cellPlayer !== p.playerId
        ? `${inning}:${cell.dataset.cellPlayer}`
        : null;
      dropRef.current = target;
      setDropTarget(target);
    };
    // Only a few rows fit on screen, so holding a dragged position near the
    // top or bottom edge scrolls the grid, faster the closer it gets. It waits
    // for a short dwell first (like the home screen), so a drop on a row that
    // happens to sit near the edge doesn't scroll out from under the finger.
    let edgeSince = 0;
    const scroller = (): HTMLElement | null => {
      for (let n = scrollRef.current?.parentElement; n; n = n.parentElement) {
        const oy = getComputedStyle(n).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return n;
      }
      return null;
    };
    const tick = () => {
      raf = 0;
      if (!press.current?.active || !lastPoint) return;
      const el = scroller();
      if (el) {
        const r = el.getBoundingClientRect();
        const edge = 36;
        const dy = lastPoint.y < r.top + edge
          ? -(r.top + edge - lastPoint.y) / 3
          : lastPoint.y > r.bottom - edge
            ? (lastPoint.y - (r.bottom - edge)) / 3
            : 0;
        if (!dy) {
          edgeSince = 0;
        } else if (!edgeSince) {
          edgeSince = performance.now();
        } else if (performance.now() - edgeSince > 350) {
          el.scrollTop += dy;
          hitTest(lastPoint.x, lastPoint.y);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    const onMove = (e: PointerEvent) => {
      const p = press.current;
      if (!p) return;
      if (!p.active) {
        // Moving before the hold completes is a scroll, not a drag.
        if (Math.hypot(e.clientX - p.x, e.clientY - p.y) > 8) reset();
        return;
      }
      lastPoint = { x: e.clientX, y: e.clientY };
      if (!raf) raf = requestAnimationFrame(tick);
      setDrag(d => (d ? { ...d, x: e.clientX, y: e.clientY } : d));
      hitTest(e.clientX, e.clientY);
    };
    const onUp = () => {
      const p = press.current;
      if (p?.active) {
        suppressUntil.current = Date.now() + 500;
        const target = dropRef.current;
        if (target) {
          const targetId = target.slice(target.indexOf(':') + 1);
          const { positionOf: posOf, onSetPosition: setPos } = latest.current;
          const fromPos = posOf(p.inning, p.playerId);
          const toPos = posOf(p.inning, targetId);
          // BENCH and EH hold any number, so moving onto them would leave the
          // dragged player's spot empty. Move the benched player instead, which
          // makes it a true swap either way.
          if (!(isBenched(fromPos) && isBenched(toPos))) {
            if (isBenched(toPos)) setPos(p.inning, targetId, fromPos);
            else setPos(p.inning, p.playerId, toPos);
            tapMedium();
          }
        }
      }
      reset();
    };
    // Once a drag is live, keep the page from scrolling under the finger, and
    // keep the release from opening the cell's picker.
    const onTouchMove = (e: TouchEvent) => { if (press.current?.active) e.preventDefault(); };
    const onTouchEnd = (e: TouchEvent) => {
      if (press.current?.active || Date.now() < suppressUntil.current) e.preventDefault();
    };
    const onClick = (e: MouseEvent) => {
      if (Date.now() < suppressUntil.current) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', reset);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: false });
    window.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', reset);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('click', onClick, true);
      reset();
    };
  }, [wide]);

  // Flag any fielding position nobody is covering, per inning, so a coach is
  // never surprised by a hole. BENCH/EH are not slots to fill.
  const required = options.filter(p => p !== 'BENCH' && p !== 'EH');
  const holes = inningIdxs
    .map(i => ({
      inning: i + 1,
      open: required.filter(pos => !(innings[i] || []).some((e: any) => e.position === pos)),
    }))
    .filter(h => h.open.length > 0);

  // Bring a focused inning into view when the grid is wider than the phone.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current?.querySelector<HTMLElement>(`[data-inning="${focusInning}"]`);
    el?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [focusInning]);

  const byId = new Map(players.map((p: any) => [p.id, p]));
  const rowProps = (player: any, rowIdx: number): RowProps => ({
    player, rowIdx, last: rowIdx === players.length - 1, inningIdxs, positionOf, options, onSetPosition,
    draggable: !!onReorder,
    wall: wide
      ? {
          totals: totals?.get(player.id),
          flash,
          dragFrom: drag ? `${drag.inning}:${drag.playerId}` : null,
          dropTarget,
          onCellPointerDown,
        }
      : undefined,
  });

  return (
    <>
      {/* Games run 4 to 7+ innings depending on age and league, so the coach
          sets how many this plan covers. Adding one builds it from the innings
          before it; removing one drops the last inning. */}
      <div className="flex items-center justify-between gap-2 mb-2 px-1">
        <span className="text-[13px] font-semibold text-ink-2 tabular-nums">
          {numInnings} {numInnings === 1 ? 'inning' : 'innings'}
        </span>
        <span className="flex items-center gap-2">
          {onRemoveInning && (
            <button
              onClick={onRemoveInning}
              className="flex items-center gap-1 h-[32px] px-3 rounded-control bg-surface border border-hairline text-[13.5px] font-semibold text-ink"
            >
              <Minus className="w-3.5 h-3.5" /> Inning
            </button>
          )}
          {onAddInning && (
            <button
              onClick={onAddInning}
              className="flex items-center gap-1 h-[32px] px-3 rounded-control bg-ink text-surface text-[13.5px] font-semibold"
            >
              <Plus className="w-3.5 h-3.5" /> Inning
            </button>
          )}
        </span>
      </div>

      <div ref={scrollRef} className="bg-surface rounded-card overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-raised">
              <th className="sticky left-0 z-10 bg-raised text-left pl-2.5 pr-1 py-1.5 text-[10px] font-bold uppercase tracking-[0.06em] text-ink-2">
                Player
              </th>
              {inningIdxs.map(i => {
                const canCopy = i < numInnings - 1 && innings[i]?.length > 0;
                return (
                  <th key={i} data-inning={i} className="px-0.5 py-1">
                    <button
                      onClick={() => canCopy && setCopyFrom(i)}
                      aria-label={canCopy ? `Inning ${i + 1}, copy forward` : `Inning ${i + 1}`}
                      className={`w-full min-w-[36px] rounded-lg py-1 text-[12px] font-bold tabular-nums ${
                        i === focusInning && focusInning > 0 ? 'bg-ink text-surface' : 'text-ink-2'
                      }`}
                    >
                      {i + 1}
                    </button>
                  </th>
                );
              })}
              {wide && (
                <>
                  <th className="pl-2 pr-1 py-1.5 text-[10px] font-bold uppercase tracking-[0.06em] text-ink-2">Field</th>
                  <th className="pl-1 pr-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.06em] text-ink-2">Bench</th>
                </>
              )}
            </tr>
          </thead>
          {onReorder ? (
            <Reorder.Group
              as="tbody"
              axis="y"
              values={players.map((p: any) => p.id)}
              onReorder={(ids: string[]) => onReorder(ids.map(id => byId.get(id)).filter(Boolean))}
            >
              {players.map((player: any, rowIdx: number) => (
                <DraggableRow key={player.id} {...rowProps(player, rowIdx)} />
              ))}
            </Reorder.Group>
          ) : (
            <tbody>
              {players.map((player: any, rowIdx: number) => (
                <tr key={player.id} className={rowIdx === players.length - 1 ? '' : 'border-b border-hairline'}>
                  <RowCells {...rowProps(player, rowIdx)} />
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      <p className="text-[12.5px] text-ink-2 px-1 pt-2">
        {onReorder
          ? 'Drag a batting number to change the order. Tap an inning number to copy it forward.'
          : 'Tap an inning number to copy it to the innings after it.'}
        {wide && ' Press and hold a position, then drag it onto another in the same inning to swap the two players.'}
      </p>

      {drag && (
        <div
          className="fixed z-50 pointer-events-none -translate-x-1/2 -translate-y-[130%] px-3 h-[34px] min-w-[44px] flex items-center justify-center rounded-lg bg-ink text-surface text-[13px] font-bold tabular-nums shadow-lg"
          style={{ left: drag.x, top: drag.y }}
        >
          {drag.label === 'BENCH' ? 'BN' : drag.label}
        </div>
      )}

      {holes.length > 0 && (
        <div className="mt-3 px-1 space-y-0.5">
          {holes.map(h => (
            <p key={h.inning} className="text-[13px] text-danger">
              Inning {h.inning}: {h.open.join(', ')} not covered
            </p>
          ))}
        </div>
      )}

      {copyFrom != null && (
        <div className="absolute inset-0 z-30 flex flex-col">
          <button className="flex-1 bg-ink/30" onClick={() => setCopyFrom(null)} aria-label="Dismiss" />
          <div className="bg-surface rounded-t-panel px-5 pt-5 pb-5 safe-bottom">
            <h3 className="text-[19px] font-bold tracking-[-0.02em] text-ink mb-1">Inning {copyFrom + 1}</h3>
            <p className="text-[14px] text-ink-2 mb-4">
              Use this defense for {copyFrom + 2 === numInnings ? `inning ${numInnings}` : `innings ${copyFrom + 2} to ${numInnings}`} too.
              It replaces what is set there now.
            </p>
            <button
              onClick={() => { const from = copyFrom; setCopyFrom(null); onCopyToLaterInnings(from); }}
              className="w-full h-[46px] rounded-control bg-ink text-surface text-[15px] font-bold"
            >
              Copy to {copyFrom + 2 === numInnings ? `inning ${numInnings}` : `innings ${copyFrom + 2} to ${numInnings}`}
            </button>
            <button
              onClick={() => setCopyFrom(null)}
              className="w-full h-[46px] mt-2 rounded-control bg-surface border border-hairline text-ink text-[15px] font-bold"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Every fill mode rebuilds all innings, so after hand edits the coach confirms
 * first. Holds the picked mode until he answers.
 */
export const RebuildDefenseSheet = ({ mode, onConfirm, onCancel }: {
  mode: string | null;
  onConfirm: (mode: string) => void;
  onCancel: () => void;
}) => {
  if (!mode) return null;
  return (
    <div className="absolute inset-0 z-30 flex flex-col">
      <button className="flex-1 bg-ink/30" onClick={onCancel} aria-label="Dismiss" />
      <div className="bg-surface rounded-t-panel px-5 pt-5 pb-5 safe-bottom">
        <h3 className="text-[19px] font-bold tracking-[-0.02em] text-ink mb-1">Rebuild the defense?</h3>
        <p className="text-[14px] text-ink-2 mb-4">
          This rebuilds every inning and replaces your changes.
        </p>
        <button
          onClick={() => onConfirm(mode)}
          className="w-full h-[46px] rounded-control bg-ink text-surface text-[15px] font-bold"
        >
          Rebuild every inning
        </button>
        <button
          onClick={onCancel}
          className="w-full h-[46px] mt-2 rounded-control bg-surface border border-hairline text-ink text-[15px] font-bold"
        >
          Keep my changes
        </button>
      </div>
    </div>
  );
};
